import {FormControl, FormControlLabel, Radio, RadioGroup} from '@material-ui/core';
import clsx from 'clsx';
import React, {ChangeEvent, useRef} from 'react';
import {Nil} from '../model/SharedTypes';
import styles from './NxRadio.scss';
import {logMissingOnChange} from '../inputError';
import {isNil} from 'lodash/fp';

export enum NxRadioVariant {
  COLUMN = 'COLUMN',
  ROW = 'ROW'
}

export interface NxRadioOption {
  disabled?: boolean;
  label: React.ReactNode;
  value: string;
  key: string;
}

export interface NxRadioProps {
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
  name?: string;
  onBlur?: (e: ChangeEvent) => void;
  onChange?: (e: ChangeEvent) => void;
  options: NxRadioOption[];
  value?: string | null;
  variant?: NxRadioVariant;
}

const calculateValue = (value: string | Nil, lastValue: string): string => {
  if (isNil(value)) {
    return value === null ? lastValue : '';
  }

  return value;
}

const NxRadio = (
  {
    ariaLabel,
    className,
    disabled,
    name,
    onBlur,
    onChange,
    options,
    value,
    variant = NxRadioVariant.ROW
  }: NxRadioProps): React.ReactElement => {

  const labelClasses = { root: styles.label };
  const radioClasses = { root: styles.radio };

  const radioGroupClassName = clsx(
    {
      [styles.row]: variant === NxRadioVariant.ROW
    }
  );

  const lastValue = useRef(value ?? '');
  const finalValue = calculateValue(value, lastValue.current);
  lastValue.current = finalValue;

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    lastValue.current = event.target.value;

    if (!onChange) {
      logMissingOnChange('Radio', name);
      return;
    }

    onChange(event);
  };

  return (
    <FormControl component="fieldset" className={className} disabled={disabled}>
      <RadioGroup
        aria-label={ariaLabel}
        className={radioGroupClassName}
        name={name}
        value={finalValue}
        onBlur={onBlur}
        onChange={handleChange}>
          {
            options.map(option => (
              <FormControlLabel classes={labelClasses}
                                control={<Radio classes={radioClasses} />}
                                key={option.key}
                                value={option.value}
                                disabled={option.disabled}
                                label={option.label} />
              )
            )
          }
      </RadioGroup>
    </FormControl>
  );
}

export default NxRadio;
